import {useEffect, useRef, useState} from 'react';
import {Box, Flex, Heading, HStack, Separator, Spinner, Text, VStack} from '@chakra-ui/react';
import {toaster} from '../ui/toaster';
import {MessageSquare} from 'lucide-react';
import {getOpenThreads} from '../../domain/comments';
import {useAddComment, useFileComments, useFileRevisions, useReplyToComment, useResolveComment} from '../../drive/queries';
import {describeDriveError} from '../../drive/driveApi';
import {CommentThreadCard} from './CommentThreadCard';
import {CommentComposer} from './CommentComposer';
import {CountBadge} from '../CountBadge';
import {slimScrollbar} from '../ui/scrollbar';
import {CommentAnchoring, CommentedPicture} from './useCommentAnchoring';
import {anchorVersion, formatRectAnchor, parseCommentAnchor} from '../../domain/commentAnchor';
import {numberRevisions} from '../../domain/revisions';

interface CommentsPanelProps {
    fileId: string;
    /** Lets new comments mark a region of the picture, and threads show theirs; none without it. */
    anchoring?: CommentAnchoring;
    picture?: CommentedPicture;
    /** The older version of the picture shown now, if one is. */
    shownRevisionId?: string;
    /** Shows a version of the picture (a thread's region was drawn on it); no option without it. */
    onViewRevision?: (revisionId: string) => void;
}

/**
 * Unresolved Drive comment threads of a file, with a composer for new comments and replies. It fills
 * the remaining height of its column: given a bounded height, the threads scroll between the title and
 * the composer; inside a scrolling container, they instead scroll with it and the composer stays stuck
 * to its bottom edge.
 */
export function CommentsPanel({fileId, anchoring, picture, shownRevisionId, onViewRevision}: CommentsPanelProps) {
    const commentsQuery = useFileComments(fileId);
    const addComment = useAddComment();
    const replyToComment = useReplyToComment();
    const resolveComment = useResolveComment();
    // A region is anchored to the current version (the last revision); shares the versions panel's cache entry.
    const revisionsQuery = useFileRevisions(anchoring ? fileId : undefined);
    const revisions = numberRevisions(revisionsQuery.data);
    const openThreads = getOpenThreads(commentsQuery.data);
    const [replyTargetId, setReplyTargetId] = useState<string | undefined>();
    // A thread that got resolved (here or elsewhere) silently stops being a reply target.
    const replyTarget = openThreads.find((c) => c.id === replyTargetId);
    const composerRef = useRef<HTMLTextAreaElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const endRef = useRef<HTMLDivElement>(null);
    const scrollToEndRef = useRef(false);

    useEffect(() => {
        setReplyTargetId(undefined);
    }, [fileId]);

    // A region just marked on the picture: on to writing the comment about it.
    const draft = anchoring?.draft;
    useEffect(() => {
        if (draft) composerRef.current?.focus();
    }, [draft]);

    // After posting a new thread, scroll the surrounding container down to it.
    useEffect(() => {
        if (!scrollToEndRef.current) return;
        scrollToEndRef.current = false;
        const list = listRef.current;
        list?.scrollTo({top: list.scrollHeight, behavior: 'smooth'});
        endRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }, [openThreads.length]);

    function handleReply(commentId: string) {
        setReplyTargetId((current) => current === commentId ? undefined : commentId);
        composerRef.current?.focus();
    }

    async function handleResolve(commentId: string) {
        try {
            await resolveComment.mutateAsync({fileId, commentId});
            toaster.create({type: 'success', title: 'Megjegyzés lezárva', duration: 1500});
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    async function handleSubmit(content: string): Promise<boolean> {
        try {
            if (replyTarget) {
                await replyToComment.mutateAsync({fileId, commentId: replyTarget.id, content});
                setReplyTargetId(undefined);
            } else {
                scrollToEndRef.current = true;
                const draft = anchoring?.draft;
                const loaded = draft && (revisionsQuery.data ?? (await revisionsQuery.refetch()).data);
                const anchor = draft ? formatRectAnchor(draft, loaded?.at(-1)?.id ?? null) : undefined;
                await addComment.mutateAsync({fileId, content, anchor});
                if (anchor) anchoring?.clearDraft();
            }
            return true;
        } catch (e) {
            scrollToEndRef.current = false;
            toaster.create({type: 'error', title: describeDriveError(e)});
            return false;
        }
    }

    return <Flex direction="column" flex="1 1 auto" minH={0} color="white">
        <VStack align="stretch" gap={3} px={4} pt={2} flexShrink={0}>
            <HStack gap={2}>
                <Heading size="sm">Megjegyzések</Heading>
                <CountBadge count={openThreads.length}/>
            </HStack>
            <Separator borderColor="whiteAlpha.300"/>
        </VStack>
        <VStack
            ref={listRef}
            align="stretch"
            gap={3}
            px={4}
            pt={3}
            pb={4}
            flex="1 1 auto"
            minH={0}
            overflowY="auto"
            css={slimScrollbar}
        >
            {commentsQuery.isLoading &&
                <HStack color="gray.400" fontSize="sm"><Spinner size="xs"/><Text>Megjegyzések betöltése…</Text></HStack>
            }
            {commentsQuery.isError &&
                <Text fontSize="sm" color="red.300">
                    A megjegyzések betöltése sikertelen: {describeDriveError(commentsQuery.error)}
                </Text>
            }
            {commentsQuery.isSuccess && openThreads.length === 0 &&
                <VStack gap={1} py={4} color="gray.500">
                    <MessageSquare size={22} strokeWidth={1.5}/>
                    <Text fontSize="sm">Nincs nyitott megjegyzés.</Text>
                </VStack>
            }
            {openThreads.map((comment) => {
                const revisionId = parseCommentAnchor(comment.anchor)?.revisionId ?? null;
                const version = anchorVersion(revisionId, revisions);
                return <CommentThreadCard
                    key={comment.id}
                    comment={comment}
                    isReplyTarget={comment.id === replyTarget?.id}
                    isResolving={resolveComment.isPending && resolveComment.variables?.commentId === comment.id}
                    onReply={() => handleReply(comment.id)}
                    onResolve={() => handleResolve(comment.id)}
                    picture={picture}
                    regionShown={anchoring?.shownCommentId === comment.id}
                    onToggleRegion={anchoring && (() => anchoring.toggleShown(comment.id))}
                    regionVersion={version}
                    viewingRegionVersion={!!revisionId && revisionId === shownRevisionId}
                    onViewRegionVersion={version.kind === 'older' && onViewRevision ? () => {
                        onViewRevision(version.revision.revision.id);
                        anchoring?.showRegion(comment.id);
                    } : undefined}
                />;
            })}
        </VStack>
        <Box position="sticky" bottom={0} zIndex={1} flexShrink={0}>
            <CommentComposer
                key={fileId}
                textareaRef={composerRef}
                replyToName={replyTarget ? (replyTarget.author?.displayName || 'Ismeretlen felhasználó') : undefined}
                isSubmitting={addComment.isPending || replyToComment.isPending}
                onCancelReply={() => setReplyTargetId(undefined)}
                onSubmit={handleSubmit}
                onQuickComment={(content) => void handleSubmit(content)}
                draw={anchoring && {
                    drawing: anchoring.drawing,
                    attached: !!anchoring.draft,
                    disabledReason: replyTarget ? 'Válaszhoz nem jelölhető ki terület' : undefined,
                    onClick: anchoring.drawing ? anchoring.cancelDrawing
                        : anchoring.draft ? anchoring.clearDraft : anchoring.startDrawing,
                }}
            />
        </Box>
        {/* Scroll target below the sticky composer, for a scrolling container around the panel. */}
        <Box ref={endRef}/>
    </Flex>;
}
